import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { ControlValueAccessor } from '@ngneat/reactive-forms';
import { interval, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';

import { FileValidationError } from '@digital-platform/shared/domain';
import { FileValidatorService } from '../file-validator/file-validator.service';
import {
  FileUpload,
  FileUploadService,
  UploadStatus,
} from './file-upload.service';

@Component({
  selector: 'digital-platform-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent<T extends FileUpload = FileUpload>
  implements OnInit, OnDestroy, ControlValueAccessor<T>
{
  @Input() public uploadLabel: string;
  @Input() public deleteLabel: string;
  @Output() public fileSelected = new EventEmitter<File>();
  @Output() public fileDeleted = new EventEmitter<T>();

  public file: T;
  public errorMessage: string;
  public isDisabled = false;
  public inProgress$: Observable<boolean>;
  public uploadPercent$: Observable<number>;

  private dialogStatusSubscriber: Subscription;

  private destroyed$ = new Subject<void>();
  private fileName = '';

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private uploadService: FileUploadService<T>,
    private validatorService: FileValidatorService
  ) {
    controlDir.valueAccessor = this;

    this.inProgress$ = this.uploadService.state$.pipe(
      filter((state) => state.fileName === this.fileName),
      map((state) =>
        [UploadStatus.Started, UploadStatus.Requested].includes(state.status)
          ? true
          : false
      ),
      shareReplay(1)
    );

    this.uploadPercent$ = this.uploadService.state$.pipe(
      filter((state) => state.fileName === this.fileName),
      map((state) => (state.progress === 0 ? null : state.progress)),
      shareReplay(1)
    );
  }

  public ngOnInit(): void {
    if (this.file) {
      const control = this.controlDir.control;
      if (control.value !== this.file) {
        control.setValue(this.file);
        control.updateValueAndValidity({ emitEvent: true });
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.dialogStatusSubscriber) {
      this.dialogStatusSubscriber.unsubscribe();
    }
  }

  public writeValue(value: T) {
    if (value) {
      this.file = value;
      this.fileName = value.fileName;
    } else {
      this.file = null;
      this.fileName = '';
    }
  }

  public onChange = (value: T) => {};
  public registerOnChange(fn: (value: T) => void) {
    this.onChange = fn;
  }

  public onTouched = () => {};
  public registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  public async fileInputChange(fileInputEvent: Event) {
    this.file = null;
    this.errorMessage = null;
    this.onTouched();

    let fileData = (<HTMLInputElement>fileInputEvent.target).files[0];
    if (!fileData) {
      return;
    }

    try {
      fileData = await this.validatorService.validate(fileData);
    } catch (error) {
      if (error instanceof FileValidationError) {
        this.handleValidationError(error);
        return;
      }
      throw error;
    }

    this.fileSelected.emit(fileData);
    this.fileName = fileData.name;

    this.uploadService.state$
      .pipe(
        filter((state) => state.status === UploadStatus.Completed),
        filter((state) => state.response.fileName === fileData.name),
        map((state) => state.response),
        take(1)
      )
      .subscribe((file) => {
        this.file = file;
        this.fileName = file.fileName;
        this.updateValue();
      });

    this.uploadService.upload(fileData);
  }

  public deleteFile() {
    this.onTouched();
    this.fileDeleted.emit(this.file);
    this.file = null;
    this.fileName = '';
    this.updateValue();
  }

  private updateValue() {
    const control = this.controlDir.control;
    control.setValue(this.file);
    control.updateValueAndValidity({ emitEvent: true });
  }

  private handleValidationError(error: FileValidationError) {
    this.errorMessage = error.message;
  }
}
